import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Target } from "lucide-react";

function GoalForm({ pt, pct, setPct, notify, setNotify, busy, onSave, onClear, onCancel }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="portfolio-target-form">
      <label className="k-sublabel">Reduce by</label>
      <input
        type="number"
        min="0"
        max="90"
        step="0.5"
        value={pct}
        onChange={(e) => setPct(e.target.value)}
        data-testid="portfolio-target-input"
        className="k-input !w-20 !py-1 text-sm"
      />
      <span className="text-xs text-[#6B7280]">% vs baseline</span>
      <label className="flex items-center gap-1.5 text-xs text-[#4D4D4D] cursor-pointer">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="portfolio-target-notify" />
        Email me if the portfolio misses it
      </label>
      <button onClick={onSave} disabled={busy} data-testid="portfolio-target-save" className="k-btn !py-1 !px-3 !text-[11px]">
        Save
      </button>
      {pt && (
        <button onClick={onClear} disabled={busy} data-testid="portfolio-target-clear" className="k-btn-outline !py-1 !px-3">
          Remove
        </button>
      )}
      <button onClick={onCancel} className="text-[11px] text-[#6B7280] hover:text-[#212121]">
        Cancel
      </button>
    </div>
  );
}

/** One company-wide reduction goal, tracked against the portfolio baseline. */
export function PortfolioTargetCard({ data, days, isAdmin, onSaved }) {
  const pt = data?.portfolio_target;
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState(pt?.target_pct ?? 5);
  const [notify, setNotify] = useState(pt?.notify !== false);
  const [busy, setBusy] = useState(false);

  const run = async (fn, message, failure) => {
    setBusy(true);
    try {
      await fn();
      toast.success(message);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(failure, { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(() => api.put("/energy/portfolio-target", { target_pct: Number(pct), notify }),
      "Portfolio goal saved", "Could not save goal");
  const clear = () =>
    run(() => api.delete("/energy/portfolio-target"), "Portfolio goal removed", "Could not remove goal");

  const progress = pt?.progress_pct ?? 0;
  const accent = pt?.met ? "#156F5B" : "#D97706";

  return (
    <div className="k-card p-6" data-testid="portfolio-target-card">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="k-label flex items-center gap-2">
            <Target className="h-3.5 w-3.5" /> Company savings goal
          </div>
          {pt ? (
            <div className="mt-2">
              <div className="text-2xl font-heading font-semibold text-[#212121]" data-testid="portfolio-target-goal">
                Cut portfolio use by {pt.target_pct}%
              </div>
              <div className="mt-1 text-sm text-[#6B7280]">
                {data.total_current_kwh?.toLocaleString()} kWh used over the last {days} days · target{" "}
                {pt.target_kwh?.toLocaleString()} kWh · baseline {data.total_baseline_kwh?.toLocaleString()} kWh
              </div>
            </div>
          ) : (
            <div className="mt-2 text-sm text-[#6B7280] max-w-md">
              Set one company-wide goal and every site's usage will be tracked against it.
            </div>
          )}
        </div>
        {pt && (
          <div className="text-right">
            <div className="k-sublabel">Status</div>
            <div className="mt-1 text-lg font-heading font-semibold" style={{ color: accent }} data-testid="portfolio-target-status">
              {pt.met ? "Goal met" : "Behind goal"}
            </div>
          </div>
        )}
      </div>

      {pt && (
        <div className="mt-5">
          <div className="flex items-center justify-between k-sublabel">
            <span>Progress to goal</span>
            <span data-testid="portfolio-target-progress">{pt.progress_pct != null ? `${pt.progress_pct}%` : "—"}</span>
          </div>
          <div className="mt-2 h-3 rounded-full bg-[#F3F4F6] overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.min(progress, 100)}%`, background: accent, transition: "width .6s ease" }} />
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="mt-5">
          {open ? (
            <GoalForm
              pt={pt}
              pct={pct}
              setPct={setPct}
              notify={notify}
              setNotify={setNotify}
              busy={busy}
              onSave={save}
              onClear={clear}
              onCancel={() => setOpen(false)}
            />
          ) : (
            <button onClick={() => setOpen(true)} data-testid="portfolio-target-edit-btn" className="k-btn-outline">
              <Target className="h-3 w-3" /> {pt ? "Edit goal" : "Set company goal"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
